// The text column is one byte, one glyph — which is what a hex dump's right-hand side has always been, and why UTF-8
// is not among these: a multi-byte character has no column of its own to sit in, and a run that starts mid-character
// would draw a different string depending on where the row happened to begin. What a selection says as UTF-8 is the
// inspector's answer instead, where a run has both of its ends.

export interface TextEncoding {
  value: string;
  label: string;
  // Null is a byte this encoding draws nothing for, which the column shows as a full stop.
  glyph(byte: number): string | null;
  byteFor(character: string): number | null;
}

export const TEXT_ENCODINGS: TextEncoding[] = [
  {
    value: "ascii",
    label: "ASCII",
    glyph: (byte) => (byte >= 0x20 && byte <= 0x7e ? String.fromCharCode(byte) : null),
    byteFor: (character) => {
      const code = character.codePointAt(0) ?? -1;
      return code >= 0x20 && code <= 0x7e ? code : null;
    },
  },
  {
    value: "latin1",
    label: "Latin-1",
    glyph: (byte) => (printableLatin1(byte) ? String.fromCharCode(byte) : null),
    byteFor: (character) => {
      const code = character.codePointAt(0) ?? -1;
      return printableLatin1(code) ? code : null;
    },
  },
  {
    value: "cp437",
    label: "CP437",
    // The DOS code page, which draws a glyph for all but one of the 256: the control codes are the arrows and the
    // card suits, and the high half is the box-drawing set every DOS-era format was laid out with. NUL is the one
    // byte still drawn as a full stop, a screen of smiling faces over a run of zeros being what somebody is reading
    // past rather than reading.
    glyph: (byte) => (byte === 0 ? null : CP437[byte]),
    byteFor: (character) => {
      // From one rather than zero: the NUL slot holds a space, and a space typed into the find box means 0x20.
      const at = CP437.indexOf(character, 1);
      return at > 0 ? at : null;
    },
  },
];

export function pickEncoding(value: string | undefined): TextEncoding {
  return TEXT_ENCODINGS.find((encoding) => encoding.value === value) ?? TEXT_ENCODINGS[0];
}

// What a whole row reads as, which is the column and not a decoding: a byte with no glyph is a full stop, so every
// character in the string stands over the byte at the same index.
export function textLine(bytes: Uint8Array, encoding: TextEncoding): string {
  let line = "";
  for (const byte of bytes) line += encoding.glyph(byte) ?? ".";
  return line;
}

// A needle typed into the find box is searched for as the column would draw it, so what is looked for is what is on
// screen. A character the encoding has no byte for is named rather than quietly dropped.
export function encodeText(text: string, encoding: TextEncoding): Uint8Array | { missing: string } {
  const characters = [...text];
  const bytes = new Uint8Array(characters.length);
  for (const [at, character] of characters.entries()) {
    const byte = encoding.byteFor(character);
    if (byte === null) return { missing: character };
    bytes[at] = byte;
  }
  return bytes;
}

function printableLatin1(code: number): boolean {
  return (code >= 0x20 && code <= 0x7e) || (code >= 0xa0 && code <= 0xff);
}

// 0x00 through 0xFF in order, sixteen to a line. The first and the last are both spaces — a NUL this table never
// draws, and the non-breaking space CP437 ends on.
const CP437 = " ☺☻♥♦♣♠•◘○◙♂♀♪♫☼"
  + "►◄↕‼¶§▬↨↑↓→←∟↔▲▼"
  + " !\"#$%&'()*+,-./"
  + "0123456789:;<=>?"
  + "@ABCDEFGHIJKLMNO"
  + "PQRSTUVWXYZ[\\]^_"
  + "`abcdefghijklmno"
  + "pqrstuvwxyz{|}~⌂"
  + "ÇüéâäàåçêëèïîìÄÅ"
  + "ÉæÆôöòûùÿÖÜ¢£¥₧ƒ"
  + "áíóúñÑªº¿⌐¬½¼¡«»"
  + "░▒▓│┤╡╢╖╕╣║╗╝╜╛┐"
  + "└┴┬├─┼╞╟╚╔╩╦╠═╬╧"
  + "╨╤╥╙╘╒╓╫╪┘┌█▄▌▐▀"
  + "αßΓπΣσµτΦΘΩδ∞φε∩"
  + "≡±≥≤⌠⌡÷≈°∙·√ⁿ²■ ";
